import { Toast, ToastTitle, ToastTrigger, Button, ToastBody, tokens, useToastController, makeStyles } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles({
  toast: {
    gridColumn: '1/4',
    paddingBlock: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalXS,
  },
});

export const RequestErrorCodes = {
  enterpriseSuspended: 'ENTERPRISE_SUSPENDED',
  invalidRequest: 'INVALID_REQUEST',
  existingEmail: 'EXISTING_EMAIL',
};

const errorCodeMapping = {
  [RequestErrorCodes.enterpriseSuspended]: 'EVENT_TAB.ERRORS.ENTERPRISE_SUSPENDED',
  [RequestErrorCodes.invalidRequest]: 'EVENT_TAB.ERRORS.INVALID_REQUEST',
  [RequestErrorCodes.existingEmail]: 'EVENT_TAB.ERRORS.EXISTING_EMAIL',
};

const useErrorHandling = (toasterId: string) => {
  const styles = useStyles();
  const { t } = useTranslation();

  const [formErrors, setFormErrors] = React.useState<Record<string, string[]>>({});

  const { dispatchToast } = useToastController(toasterId);

  const clearErrors = React.useCallback((field: string) => {
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const handleError = React.useCallback(
    async (errorResponse, clearInvalidProjects: () => Promise<void>) => {
      let errorList = [];
      let error = t('EVENT_TAB.ERRORS.GENERIC_ERROR');
      if (errorResponse?.data?.code) {
        error = t(errorCodeMapping[errorResponse.data.code] ?? 'EVENT_TAB.ERRORS.GENERIC_ERROR');
        if (errorResponse.data.code === RequestErrorCodes.invalidRequest) {
          setFormErrors(errorResponse.data.errors);
          errorList = Object.values(errorResponse.data.errors).flatMap((x) => x);
          if (errorResponse.data.errors.projectIds) {
            await clearInvalidProjects();
          }
        }
      }
      dispatchToast(
        <Toast>
          <ToastTitle
            action={
              <ToastTrigger>
                <Button
                  appearance="transparent"
                  icon={<DismissRegular />}
                />
              </ToastTrigger>
            }
          >
            {t('EVENT_TAB.ERRORS.TITLE')}
          </ToastTitle>
          <ToastBody className={styles.toast}>
            {error}
            {errorList.length > 0 && (
              <ul>
                {errorList.map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            )}
          </ToastBody>
        </Toast>,
        { intent: 'error', position: 'bottom' },
      );
    },
    [dispatchToast, styles.toast],
  );

  return { formErrors, handleError, clearErrors };
};

export default useErrorHandling;
